/** "Import design" — a 2-step wizard dialog (Figma plan §7 / Next steps 2).
 *
 *  Step 1: tenant + source (Figma URL, HTML file upload, or pasted HTML) →
 *          creates a durable Notification_Material (uploads stage to S3 first,
 *          the ai_import file_key discipline).
 *  Step 2: map designs to notifications. Figma materials first run a
 *          `discover` background task that expands the link into template
 *          frames with PROPOSED events (batch.propose_events) — proposals,
 *          never silent decisions, because the event decides the context
 *          schema everything downstream validates against. Several frames can
 *          be selected at once: one import run builds ONE proposal with a
 *          notification per design and SHARED block/tag/theme proposals.
 *
 *  On start the wizard closes and lands on the task's live logs
 *  (/bgtasks/:id?tab=logs); a detached watcher (watchIngestTask) offers
 *  "Review proposal" when ingest completes — the proposal is also always
 *  reachable under Design Imports → Proposals.
 */
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  LinearProgress,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as bgtasksApi from '@/api/bgtasks';
import { TASK_STATE, isActiveState } from '@/api/bgtasks';
import { ConsoleApiError } from '@/api/client';
import * as proposalsApi from '@/api/compositionProposals';
import * as materialsApi from '@/api/materials';
import type { FigmaFrameProposal, MaterialListItem } from '@/api/materials';
import { listEvents, type NotificationEvent } from '@/api/notifications';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { useCreateDialogTenant } from '@/scope/useCreateDialogTenant';

import { watchIngestTask } from './watchIngestTask';

const FIGMA_URL_RE = /^https:\/\/(www\.)?figma\.com\/(design|file)\/[A-Za-z0-9]{8,}/;
type SourceTab = 'figma' | 'file' | 'paste';

/** Backend cap (start_ingest MAX_INGEST_FRAMES) — one reviewable proposal. */
const MAX_FRAMES = 12;

interface FrameConfig {
  selected: boolean;
  event: string;
  name: string;
}

/** Poll one background task until it leaves an active state. */
function useTaskPoll(taskId: string | null) {
  return useQuery({
    queryKey: ['bgtask', taskId],
    queryFn: () => bgtasksApi.retrieve(taskId!),
    enabled: !!taskId,
    refetchInterval: (q) => (q.state.data && isActiveState(q.state.data.state) ? 2_000 : false),
  });
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Re-ingest path: skip step 1 and use this existing material. */
  material?: MaterialListItem | null;
  /** The page's operators (D9), for a new import — see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
}

export function ImportDesignWizard({ open, onClose, material: presetMaterial, pageTenants }: Props) {
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [activeStep, setActiveStep] = useState(0);
  const [tenant, setTenant, resetTenant] = useCreateDialogTenant(open, pageTenants);
  const [sourceTab, setSourceTab] = useState<SourceTab>('figma');
  const [figmaUrl, setFigmaUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [pastedHtml, setPastedHtml] = useState('');
  const [title, setTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [material, setMaterial] = useState<MaterialListItem | null>(null);

  const [discoverTaskId, setDiscoverTaskId] = useState<string | null>(null);
  const [frameConfigs, setFrameConfigs] = useState<Record<string, FrameConfig>>({});
  // Single-design (HTML) fields.
  const [eventCode, setEventCode] = useState('');
  const [notificationName, setNotificationName] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [starting, setStarting] = useState(false);

  // Re-ingest an existing material: the dialog opens directly on step 2.
  useEffect(() => {
    if (!open) return;
    if (presetMaterial) {
      setMaterial(presetMaterial);
      setTenant({
        id: presetMaterial.tour_operator.id,
        name: presetMaterial.tour_operator.name,
        ...(presetMaterial.tour_operator.code ? { code: presetMaterial.tour_operator.code } : {}),
      });
      setTitle(presetMaterial.title);
      setActiveStep(1);
      if (presetMaterial.kind === 'figma' && !presetMaterial.figma) {
        void runDiscover(presetMaterial);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, presetMaterial?.id]);

  const reset = () => {
    setActiveStep(0);
    resetTenant();
    setSourceTab('figma');
    setFigmaUrl('');
    setFile(null);
    setPastedHtml('');
    setTitle('');
    setCreating(false);
    setMaterial(null);
    setDiscoverTaskId(null);
    setFrameConfigs({});
    setEventCode('');
    setNotificationName('');
    setEmailSubject('');
    setStarting(false);
  };

  const closeAndReset = () => {
    reset();
    onClose();
  };

  const eventsQuery = useQuery({
    queryKey: ['notification-events'],
    queryFn: () => listEvents(),
    staleTime: 5 * 60_000,
    enabled: open,
  });
  const eventOptions: NotificationEvent[] = eventsQuery.data?.events ?? [];

  // ---- step 1: create the material -----------------------------------------

  const sourceReady =
    tenant !== null &&
    (sourceTab === 'figma'
      ? FIGMA_URL_RE.test(figmaUrl.trim())
      : sourceTab === 'file'
        ? !!file
        : pastedHtml.trim().length > 0);

  const defaultTitle = () => {
    if (title.trim()) return title.trim();
    if (sourceTab === 'file' && file) return file.name;
    if (sourceTab === 'figma') {
      const segment = figmaUrl.split('/').filter(Boolean).pop() ?? '';
      return decodeURIComponent(segment.split('?')[0] ?? '') || 'Figma design';
    }
    return 'Pasted HTML design';
  };

  const runDiscover = async (target: MaterialListItem) => {
    try {
      const started = await proposalsApi.discover({
        tour_operator_id: target.tour_operator.id,
        material_id: target.id,
      });
      setDiscoverTaskId(started.task_id);
    } catch (e) {
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Frame discovery failed to start.', 'error');
    }
  };

  const createMaterial = async () => {
    if (!tenant) return;
    setCreating(true);
    try {
      let body: materialsApi.MaterialCreate;
      if (sourceTab === 'figma') {
        body = { tour_operator: tenant.id, kind: 'figma', title: defaultTitle(), url: figmaUrl.trim() };
      } else if (sourceTab === 'file') {
        const staged = await materialsApi.upload(file!);
        body = { tour_operator: tenant.id, kind: 'html', title: defaultTitle(), file_key: staged.file_key };
      } else {
        body = { tour_operator: tenant.id, kind: 'html', title: defaultTitle(), html: pastedHtml };
      }
      const created = await materialsApi.create(body);
      setMaterial(created);
      setActiveStep(1);
      if (created.kind === 'figma') void runDiscover(created);
    } catch (e) {
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not create the material.', 'error');
    } finally {
      setCreating(false);
    }
  };

  // ---- step 2: frames → notifications ---------------------------------------

  const discoverTask = useTaskPoll(discoverTaskId);
  const discoverDone = discoverTask.data?.state === TASK_STATE.COMPLETED;
  const discoverFailed = discoverTask.data?.state === TASK_STATE.FAILED;

  const materialDetail = useQuery({
    queryKey: ['material', material?.id, discoverDone],
    queryFn: () => materialsApi.retrieve(material!.id),
    enabled: !!material && (material.kind !== 'figma' || discoverDone || !!material.figma),
  });
  const frames: FigmaFrameProposal[] = useMemo(
    () => materialDetail.data?.figma?.frames ?? material?.figma?.frames ?? [],
    [materialDetail.data, material],
  );
  const isFigma = material?.kind === 'figma';
  const discoverPending = isFigma && !!discoverTaskId && !discoverDone && !discoverFailed;

  // Seed per-frame configs once per frame list: proposed events pre-fill the
  // pickers, single-frame links start selected, multi-frame links start
  // unselected (the operator picks — proposals, never silent decisions).
  useEffect(() => {
    if (frames.length === 0) return;
    setFrameConfigs((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const seeded: Record<string, FrameConfig> = {};
      for (const frame of frames) {
        seeded[frame.frame_id] = {
          selected: frames.length === 1,
          event: frame.event_code ?? '',
          name: frame.frame_name,
        };
      }
      return seeded;
    });
  }, [frames]);

  const patchFrame = (frameId: string, patch: Partial<FrameConfig>) =>
    setFrameConfigs((prev) => ({
      ...prev,
      [frameId]: { ...(prev[frameId] ?? { selected: false, event: '', name: '' }), ...patch },
    }));

  const selectedFrames = frames.filter((f) => frameConfigs[f.frame_id]?.selected);
  const framesReady =
    selectedFrames.length > 0 &&
    selectedFrames.length <= MAX_FRAMES &&
    selectedFrames.every((f) => (frameConfigs[f.frame_id]?.event ?? '').trim().length > 0);
  const startReady = isFigma && frames.length > 0 ? framesReady : eventCode.trim().length > 0;

  const startIngest = async () => {
    if (!material || !tenant) return;
    setStarting(true);
    try {
      const body: Parameters<typeof proposalsApi.startIngest>[0] = {
        tour_operator_id: tenant.id,
        material_id: material.id,
      };
      if (isFigma && frames.length > 0) {
        body.frames = selectedFrames.map((frame) => {
          const config = frameConfigs[frame.frame_id]!;
          return {
            node_id: frame.frame_id,
            event_code: config.event.trim(),
            ...(config.name.trim() ? { notification_name: config.name.trim() } : {}),
          };
        });
      } else {
        body.event_code = eventCode.trim();
        if (notificationName.trim()) body.notification_name = notificationName.trim();
        if (emailSubject.trim()) body.email_subject = emailSubject.trim();
      }
      const started = await proposalsApi.startIngest(body);
      const count = body.frames?.length ?? 1;
      snackbar.show(
        `Import started — templatizing ${count} design${count === 1 ? '' : 's'}. Watch the logs; you'll be offered the review when it's done.`,
        'success',
      );
      watchIngestTask(started.task_id, snackbar);
      closeAndReset();
      navigate(`/bgtasks/${started.task_id}?tab=logs`);
    } catch (e) {
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Import failed to start.', 'error');
      setStarting(false);
    }
  };

  return (
    <AppDialog open={open} onClose={closeAndReset} size="md" title="Import design" disableBackdropClose>
      <Stepper activeStep={activeStep} orientation="vertical">
        <Step>
          <StepLabel>{material ? `Source: ${material.title}` : 'Tenant and source'}</StepLabel>
          <StepContent>
            <Box sx={{ maxWidth: 420, mb: 2 }}>
              <TenantPicker
                value={tenant}
                featured={pageTenants}
                onChange={setTenant}
                label="Tenant (required)"
              />
            </Box>
            <Tabs value={sourceTab} onChange={(_, v) => setSourceTab(v as SourceTab)} sx={{ mb: 2 }}>
              <Tab value="figma" label="Figma URL" />
              <Tab value="file" label="HTML file" />
              <Tab value="paste" label="Paste HTML" />
            </Tabs>

            {sourceTab === 'figma' && (
              <TextField
                fullWidth
                size="small"
                label="Figma design URL"
                placeholder="https://figma.com/design/<file>/<name>?node-id=…"
                value={figmaUrl}
                onChange={(e) => setFigmaUrl(e.target.value)}
                helperText="A frame link imports that frame; a page/section link lists every template frame in the next step."
              />
            )}
            {sourceTab === 'file' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".html,.htm,text/html"
                  style={{ display: 'none' }}
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileInputRef.current?.click()}>
                  Choose HTML file
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {file ? file.name : 'No file selected.'}
                </Typography>
              </Box>
            )}
            {sourceTab === 'paste' && (
              <TextField
                fullWidth
                multiline
                minRows={6}
                maxRows={14}
                size="small"
                label="HTML source"
                value={pastedHtml}
                onChange={(e) => setPastedHtml(e.target.value)}
              />
            )}

            <TextField
              fullWidth
              size="small"
              label="Title"
              placeholder={defaultTitle()}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              helperText="How this material appears in the Design Imports grid."
              sx={{ mt: 2 }}
            />

            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                disabled={!sourceReady || creating}
                onClick={() => void createMaterial()}
                startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                Next: map designs to notifications
              </Button>
            </Box>
          </StepContent>
        </Step>

        <Step>
          <StepLabel>Map designs to notifications</StepLabel>
          <StepContent>
            {discoverPending && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Reading the Figma file and detecting template frames…
                </Typography>
                <LinearProgress />
              </Box>
            )}
            {discoverFailed && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Frame discovery failed: {discoverTask.data?.error_message || 'see Background Tasks.'} You can
                still pick an event and import the frame the URL points at.
              </Alert>
            )}

            {isFigma && frames.length > 0 ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {frames.length} template frame{frames.length === 1 ? '' : 's'} found. Select the ones to
                  import — one run builds ONE proposal with a notification per design, and elements shared
                  between designs (headers, footers, tags) are proposed once. Suggested events are
                  proposals, never silent decisions.
                </Typography>
                {selectedFrames.length > MAX_FRAMES && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    At most {MAX_FRAMES} designs per import — split larger batches.
                  </Alert>
                )}
                {frames.map((frame) => {
                  const config = frameConfigs[frame.frame_id] ?? { selected: false, event: '', name: '' };
                  return (
                    <Box
                      key={frame.frame_id}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        p: 0.75,
                        borderRadius: 1,
                        bgcolor: config.selected ? 'action.selected' : undefined,
                        '&:hover': { bgcolor: config.selected ? 'action.selected' : 'action.hover' },
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={config.selected}
                        onChange={(e) => patchFrame(frame.frame_id, { selected: e.target.checked })}
                        sx={{ mt: 0.5 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {frame.frame_name}
                          </Typography>
                          {frame.event_code ? (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`suggested: ${frame.event_code} (${Math.round(frame.confidence * 100)}%)`}
                            />
                          ) : (
                            <Chip size="small" variant="outlined" color="warning" label="no event suggested" />
                          )}
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                            {frame.reason}
                          </Typography>
                        </Box>
                        {config.selected && (
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                              gap: 1,
                              mt: 1,
                            }}
                          >
                            <Autocomplete<NotificationEvent>
                              size="small"
                              options={eventOptions}
                              loading={eventsQuery.isLoading}
                              value={eventOptions.find((e) => e.code === config.event) ?? null}
                              onChange={(_, v) => patchFrame(frame.frame_id, { event: v?.code ?? '' })}
                              getOptionLabel={(o) => o.code}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Event"
                                  error={!config.event}
                                  placeholder="pick the target event"
                                />
                              )}
                            />
                            <TextField
                              size="small"
                              label="Notification name"
                              value={config.name}
                              onChange={(e) => patchFrame(frame.frame_id, { name: e.target.value })}
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              !discoverPending && (
                <Box sx={{ mb: 2 }}>
                  <Autocomplete<NotificationEvent>
                    size="small"
                    options={eventOptions}
                    loading={eventsQuery.isLoading}
                    value={eventOptions.find((e) => e.code === eventCode) ?? null}
                    onChange={(_, v) => setEventCode(v?.code ?? '')}
                    getOptionLabel={(o) => o.code}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Event"
                        helperText="Decides the context schema and permitted features for everything downstream."
                      />
                    )}
                    sx={{ maxWidth: 480, mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Notification name"
                    value={notificationName}
                    onChange={(e) => setNotificationName(e.target.value)}
                    sx={{ maxWidth: 480, mb: 2, display: 'block' }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Email subject (templatized too)"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    sx={{ maxWidth: 480, display: 'block' }}
                  />
                </Box>
              )
            )}

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {!presetMaterial && (
                <Button onClick={() => setActiveStep(0)} disabled={starting}>
                  Back
                </Button>
              )}
              <Button
                variant="contained"
                disabled={!startReady || starting || discoverPending}
                onClick={() => void startIngest()}
                startIcon={starting ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {isFigma && frames.length > 0
                  ? `Import ${selectedFrames.length || ''} design${selectedFrames.length === 1 ? '' : 's'}`
                  : 'Start import'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                You&apos;ll land on the task&apos;s live logs; review follows when it finishes.
              </Typography>
            </Box>
          </StepContent>
        </Step>
      </Stepper>
    </AppDialog>
  );
}
